import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { Id } from '@/infra/id'
import { type JobCreationRootViewModel } from '@/features/jobs/drafting/JobCreationRootViewModel'
import { PrefilledDraftInput } from '@/features/jobs/drafting/PrefilledDraftInput'
import { task } from 'mobx-task'
import { links } from '@/utils/link'
import type { AlertFactory } from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import { QuestionStepViewModel } from '@/features/jobs/drafting/IntakeQuestionsViewModel'
import { whileActive } from '@/utils/rx-util'
import { tap } from 'rxjs/operators'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { match } from '@/utils/pattern-match-util'

/**
 * The steps of the dialog.
 */
export type ModalStep = 'Questions' | 'Billing'

/**
 * Whether the dialog creates a job or edits existing ones.
 */
export type ModalType = 'NewDraft' | 'EditDraft'

/**
 * The dialog's header content.
 */
export interface ModalHeader {
  title: string
  description: string
  pager: string | null
}

/**
 * Dialog for drafting a new job, or editing the responses of existing ones.
 */
export class CreateJobDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The dialog state. Closing it resets the dialog.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The step being shown.
   */
  @observable accessor modalStep: ModalStep = 'Questions'

  /**
   * Whether the dialog creates a job or edits existing ones.
   */
  @observable accessor modalType: ModalType = 'NewDraft'

  /**
   * The jobs being edited, empty when creating a job.
   */
  @observable accessor jobIds: Id[] = []

  /**
   * The index in {@link jobIds} of the job being edited.
   */
  @observable accessor currentJobIndex = 0

  constructor(
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
    private readonly _jobCreationRootVm: JobCreationRootViewModel,
  ) {
    this._lifecycle.manage(
      this._jobCreationRootVm.jobCreated$.pipe(
        tap(() =>
          this._newAlert()
            .saying('Success!', 'Job was created')
            .withState('success')
            .autoDismiss()
            .show(),
        ),
        whileActive(this.modal.showing$),
      ),
    )
  }

  /**
   * The draft editor.
   */
  @computed
  get editor() {
    return this._jobCreationRootVm.current
  }

  /**
   * Whether the billing step can be shown.
   */
  @computed
  get isBillingStepReady() {
    return this.editor.isSubmissionAvailable
  }

  /**
   * Whether the current user may view the draft editor.
   */
  @computed
  get canViewEditor() {
    return this._jobCreationRootVm.canViewEditor
  }

  /**
   * Whether the draft has been saved as a job.
   */
  @computed
  get jobWasCreated() {
    return this.editor.jobWasCreated
  }

  /**
   * Whether the current user pays for the job, `undefined` until that is known.
   */
  @computed
  get isBillingRelevant() {
    if (!this.editor.job.owner.isCurrentUser) {
      return false
    }

    return this.editor.isSubmissionAvailable
      ? this.editor.submission.billing.isBillingRelevant
      : undefined
  }

  /**
   * The draft's title.
   */
  @computed
  get draftTitle() {
    return this.editor.job.displayName.trim()
  }

  /**
   * The dialog's header content.
   */
  @computed
  get modalHeader(): ModalHeader {
    if (this.modalStep === 'Billing') {
      return {
        title: 'Add payment details',
        description: 'Select payment method and review summary',
        pager: '2 of 2',
      }
    }

    return {
      title: match(this.modalType, {
        NewDraft: 'Create Job',
        EditDraft: 'Edit responses',
      }),
      description: 'We will automatically save your progress.',
      pager: this._questionsPager,
    }
  }

  /**
   * Whether the created job can be opened.
   */
  @computed
  get canViewDetail() {
    return this.modalType === 'NewDraft' && this.editor.jobWasCreated
  }

  /**
   * How many questions are not answered yet.
   */
  @computed
  get incompleteQuestionsCount() {
    return this.editor.outline.stepAccessors.filter(
      (a) => a instanceof QuestionStepViewModel && a.isIncomplete,
    ).length
  }

  /**
   * Whether several jobs are edited one after another.
   */
  @computed
  get isBulkEdit() {
    return this.jobIds.length > 1
  }

  /**
   * Whether the bulk edit can move back to the previous job.
   */
  @computed
  get isBackShown() {
    return this.currentJobIndex !== 0
  }

  /**
   * Whether incomplete questions are flagged, which they are only when
   * editing existing drafts.
   */
  @computed
  get isValidationShown() {
    return this.modalType === 'EditDraft'
  }

  /**
   * Whether the unanswered questions feedback is shown.
   */
  @computed
  get isUnansweredFeedbackShown() {
    return this.isValidationShown && this.incompleteQuestionsCount > 0
  }

  /**
   * The label of the button that moves to the next job.
   */
  @computed
  get bulkEditActionButtonText() {
    return this.currentJobIndex + 1 === this.jobIds.length ? 'Complete' : 'Next'
  }

  /**
   * The pager of the questions step: the step of two when billing follows,
   * otherwise the job being edited, if there are several.
   */
  @computed
  private get _questionsPager() {
    if (this.isBillingRelevant) {
      return '1 of 2'
    }

    if (this.isBulkEdit) {
      return `${this.currentJobIndex + 1} of ${this.jobIds.length}`
    }

    return null
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Resets the dialog to the first step of a new draft.
   */
  @action.bound
  reset() {
    this.modalStep = 'Questions'
    this.modalType = 'NewDraft'
    this.currentJobIndex = 0
    this.jobIds = []
  }

  /**
   * Opens the dialog for the given jobs, or for a new draft when none are given.
   */
  @action.bound
  open(jobIds?: Id[], prefilledDraftInput?: PrefilledDraftInput) {
    const jobId = jobIds?.[0]
    this.jobIds = jobIds ?? []
    this.modal.show()
    this.modalType = jobIds ? 'EditDraft' : 'NewDraft'
    return this.edit(jobId, prefilledDraftInput)
  }

  /**
   * Shows the given step.
   */
  @action.bound
  setStep(step: ModalStep) {
    this.modalStep = step
  }

  /**
   * Shows the questions step.
   */
  @action.bound
  showQuestionsStep() {
    this.setStep('Questions')
  }

  /**
   * Shows the billing step.
   */
  @action.bound
  showBillingStep() {
    this.setStep('Billing')
  }

  /**
   * Closes the dialog and opens the created job.
   */
  @action.bound
  goDetail() {
    this.modal.close()
    this._navigate(links.job(Id.assert(this.editor.job.id)))
  }

  /**
   * Opens the editor for the given job, or for a new draft.
   */
  edit = task(async (jobId?: Id, prefilledDraftInput?: PrefilledDraftInput) => {
    return await this._jobCreationRootVm.edit(
      jobId,
      prefilledDraftInput ?? PrefilledDraftInput.empty(),
    )
  })

  /**
   * Moves to the next job being edited, closing the dialog after the last one.
   */
  @action.bound
  nextJob() {
    this.currentJobIndex = this.currentJobIndex + 1

    if (this.currentJobIndex === this.jobIds.length) {
      this.modal.close()

      this._newAlert()
        .withState('success')
        .saying('Great Job!', `You've successfully edited the job responses`)
        .autoDismiss()
        .show()

      return
    }

    this._editCurrentJob()
  }

  /**
   * Moves to the previous job being edited.
   */
  @action.bound
  previousJob() {
    this.currentJobIndex = this.currentJobIndex - 1
    this._editCurrentJob()
  }

  /**
   * Opens the editor for the current job.
   *
   * @private
   */
  private _editCurrentJob() {
    // The dialog renders a failure from the `edit` task's state.
    this.edit(this.jobIds[this.currentJobIndex]).catch(() => undefined)
  }
}
